import moment from "moment";
import React, { useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";
import html2canvas from "html2canvas";
import { useTranslation } from "react-i18next";

const GraphChart = ({
  booksData = [],
  graphHeader,
  domainRange = [0, 100],
  type = "",
  selectedEvent,
}) => {
  const isMobile = window.innerWidth <= 768;
  const { t } = useTranslation();
  const [isButtonVisible, setIsButtonVisible] = useState(true);

  const currentYear = moment().year();
  const previousYear = currentYear - 1;

  const isYearlyData =
    type === "yearly" &&
    booksData.length > 0 &&
    booksData[0].name !== undefined;

  let data;

  if (isYearlyData) {
    data = booksData;
  } else {
    data = Array.from({ length: 12 }, (_, i) => {
      const monthIndex = i + 1;
      const rawMonth = moment().month(i).format("MMM");
      const monthName = t(rawMonth); // Translate "Jan" → "जनवरी", etc.

      const currentYearPoint = booksData.find(
        (pt) => pt.year === currentYear && pt.month === monthIndex
      );
      const previousYearPoint = booksData.find(
        (pt) => pt.year === previousYear && pt.month === monthIndex
      );

      return {
        name: monthName,
        [currentYear]: currentYearPoint?.points ?? 0,
        [previousYear]: previousYearPoint?.points ?? 0,
      };
    });
  }

  let maxDomainRange = domainRange;

  if (isYearlyData) {
    const allPoints = booksData.flatMap((item) => [
      item.currentYear,
      item.previousYear,
    ]);
    if (allPoints.length > 0) {
      const maxScore = Math.max(...allPoints);
      let tickInterval;
      if (maxScore > 1000000) tickInterval = 100000;
      else if (maxScore > 100000) tickInterval = 10000;
      else if (maxScore > 10000) tickInterval = 1000;
      else if (maxScore > 1000) tickInterval = 100;
      else tickInterval = 10;

      const roundedMaxScore = Math.ceil(maxScore / tickInterval) * tickInterval;
      maxDomainRange = [0, roundedMaxScore + tickInterval * 1];
    }
  } else {
    const allPoints = booksData.map((point) => point.points);
    if (allPoints.length > 0) {
      const maxScore = Math.max(...allPoints);
      let tickInterval;
      if (maxScore > 1000000) tickInterval = 100000;
      else if (maxScore > 100000) tickInterval = 10000;
      else if (maxScore > 10000) tickInterval = 1000;
      else if (maxScore > 1000) tickInterval = 100;
      else tickInterval = 10;

      const roundedMaxScore = Math.ceil(maxScore / tickInterval) * tickInterval;
      maxDomainRange = [0, roundedMaxScore + tickInterval * 1];
    }
  }

  const handleDownload = () => {
    setIsButtonVisible(false);
    setTimeout(() => {
      html2canvas(document.querySelector("#" + type)).then((canvas) => {
        const link = document.createElement("a");
        link.href = canvas.toDataURL("image/png");
        link.download = `${type}-graph.png`;
        link.click();
      });
    }, 1000);
    setTimeout(() => setIsButtonVisible(true), 1000);
  };

  const formatNumberWithCommas = (number) =>
    Math.ceil(number).toLocaleString("en-IN");

  const CustomTooltip = ({ payload, label, active }) => {
    if (active && payload && payload.length) {
      return (
        <div className="user-welcome-card">
          <p className="m-0 filter-text text-dark">{t(label)}</p>
          {payload.map((item) => (
            <p key={item.dataKey} className="m-0 filter-text last-year-tooltip">
              {`${item.dataKey}: ${formatNumberWithCommas(item.value)}`}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const CustomXAxisTick = ({ x, y, payload }) => {
    const rotationAngle = isMobile ? -15 : 0;
    return (
      <g transform={`translate(${x},${y})`}>
        <text
          x={0}
          y={isMobile ? 0 : 10}
          dy={0}
          textAnchor={isMobile ? "end" : "middle"}
          fill="#6e7fa3"
          transform={`rotate(${rotationAngle})`}
          fontSize={isMobile ? 10 : 13}
        >
          {payload.value}
        </text>
      </g>
    );
  };

  const CustomYAxisTick = ({ x, y, payload }) => {
    return (
      <text
        x={x}
        y={y}
        textAnchor="end"
        fill="#6e7fa3"
        fontSize={isMobile ? 10 : 13}
        dy={3}
      >
        {formatNumberWithCommas(payload.value)}
      </text>
    );
  };

  const RoundedBar = ({ fill, x, y, width, height }) => {
    if ([x, y, width, height].some((val) => isNaN(val))) return null;
    const radius = 5;
    const effectiveRadius = Math.min(radius, height / 2);
    return (
      <g>
        <path
          d={`
            M${x + effectiveRadius},${y}
            L${x + width - effectiveRadius},${y}
            Q${x + width},${y} ${x + width},${y + effectiveRadius}
            L${x + width},${y + height}
            L${x},${y + height}
            L${x},${y + effectiveRadius}
            Q${x},${y} ${x + effectiveRadius},${y}
            Z
          `}
          fill={fill}
        />
      </g>
    );
  };

  const CustomLegend = () => {
    return (
      <div
        className="custom-legend-container"
        style={{
          display: "flex",
          justifyContent: "flex-start",
          margin: "10px 0 0 20px",
        }}
      >
        <div
          className="legend-item"
          style={{ display: "flex", alignItems: "center", marginRight: "15px" }}
        >
          <div
            className="legend-dot"
            style={{
              width: "12px",
              height: "12px",
              borderRadius: "50%",
              backgroundColor: "#A493F2", // current year
              marginRight: "5px",
            }}
          ></div>
          <span>{currentYear}</span>
        </div>
        <div
          className="legend-item"
          style={{ display: "flex", alignItems: "center" }}
        >
          <div
            className="legend-dot"
            style={{
              width: "12px",
              height: "12px",
              borderRadius: "50%",
              backgroundColor: "#C588AC", // previous year
              marginRight: "5px",
            }}
          ></div>
          <span>{previousYear}</span>
        </div>
      </div>
    );
  };

  const renderBars = () => {
    if (isYearlyData) {
      return (
        <>
          <Bar
            dataKey={String(currentYear)}
            fill="#A493F2"
            barSize={isMobile ? 8 : 15}
            shape={<RoundedBar />}
            name={`${currentYear}`}
          />
          <Bar
            dataKey={String(previousYear)}
            fill="#C588AC"
            barSize={isMobile ? 8 : 15}
            shape={<RoundedBar />}
            name={`${previousYear}`}
          />
        </>
      );
    } else {
      return (
        <>
          <Bar
            dataKey={String(previousYear)}
            fill="#C588AC"
            barSize={isMobile ? 8 : 15}
            shape={<RoundedBar />}
            name={`${previousYear}`}
          />
          <Bar
            dataKey={String(currentYear)}
            fill="#A493F2"
            barSize={isMobile ? 8 : 15}
            shape={<RoundedBar />}
            name={`${currentYear}`}
          />
        </>
      );
    }
  };

  const chartContainerClass =
    type === "monthly" &&
    (selectedEvent === "Regular Update" || selectedEvent === "Gita Marathon")
      ? "books-graph-card wide-graph"
      : "books-graph-card";

  return (
    <div className="graph-container-card graph-container" id={type}>
      <div className="head-sec">
        <div className="temple-list-head-sec">
          <h2 className="pledge-head-temple-points">
            {graphHeader}
            <span style={{ fontSize: "12px" }}>
              (
              {graphHeader === "Year Wise Score"
                ? t("Points, your temple")
                : t("Count, your temple")}
              )
            </span>
          </h2>

          <div className="d-flex align-items-center">
            <span className="previous-year-span"></span>&nbsp;&nbsp;
            <span className="years-mobile">{previousYear}</span>
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
            <span className="current-year-span"></span>&nbsp;&nbsp;
            <span className="years-mobile">{currentYear}</span>
          </div>
        </div>
        {isButtonVisible && (
          <span className="button-text-download" onClick={handleDownload}>
            {t("Download")}
          </span>
        )}
      </div>

      <div className={`${chartContainerClass} mobile-wide-graph`}>
        <ResponsiveContainer width="100%" height={isMobile ? 370 : 400}>
          <BarChart
            data={data}
            margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              vertical={false}
              horizontal={true}
            />
            <XAxis
              dataKey="name"
              textAnchor="end"
              interval={0}
              tickLine={false}
              tick={<CustomXAxisTick />}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              interval={1}
              tickCount={15}
              domain={maxDomainRange}
              tick={<CustomYAxisTick />}
            />

            <Tooltip content={<CustomTooltip />} />
            <Legend wrapperStyle={{ display: "none" }} />
            {renderBars()}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default GraphChart;
